/**
 * home-accent.ts - Homepage redesign accent helper (Wave 0 foundation).
 *
 * Token-driven port of the design source's `acc()` method. The design used
 * literal `hsl(...)` strings; the repo already defines byte-identical
 * `--accent-<name>-{ink,mid,tint}` tokens (and Wave 0 added `-glow`), so
 * `acc()` returns those token strings instead of bare literals. This keeps
 * every viz/section reading from one source of truth.
 *
 * Consumed via raw `hsl(var(--accent-*))` strings in inline SVG/styles -
 * NOT through Tailwind utilities (these tokens are deliberately not
 * registered in `@theme inline`).
 */

export type HomeAccent = "amber" | "sky" | "emerald" | "rose" | "violet";

export interface AccentTokens {
  ink: string;
  mid: string;
  tint: string;
  glow: string;
}

/**
 * Returns the four accent token strings for a homepage accent name.
 * Mirrors the design's `acc(name)` shape `{ ink, mid, tint, glow }`.
 */
export function acc(name: HomeAccent): AccentTokens {
  return {
    ink: `hsl(var(--accent-${name}-ink))`,
    mid: `hsl(var(--accent-${name}-mid))`,
    tint: `hsl(var(--accent-${name}-tint))`,
    glow: `hsl(var(--accent-${name}-glow))`,
  };
}

/**
 * LITERAL_EXCEPTIONS - off-token colors that the design hardcodes and that
 * MUST be reproduced verbatim (do NOT route these through `acc()` or
 * approximate them with tokens). Each value is the exact `hsl(...)` string
 * from the design source, with the line it appears on noted for audit.
 *
 * These are intentionally one-off panel/surface tints and chrome dots that
 * don't map cleanly onto the ink/mid/tint/glow scale. Later-wave section
 * agents copy them in as inline-style literals.
 */
export const LITERAL_EXCEPTIONS = {
  /* "Where it breaks" problem cards - very light accent panel tints (lighter
     than the `-tint` token) + card-1 top bar. Cards are pillar-mapped:
     1 amber (Submit clean), 2 emerald (Win the payer game), 3 sky (See it all). */
  problemCard1PanelBg: "hsl(40 96% 95%)", // card 1 (amber) viz panel bg
  problemCard1TopBar: "hsl(38 92% 50%)", // card 1 top accent bar (bright amber)
  problemCard2PanelBg: "hsl(150 50% 95%)", // card 2 (emerald) viz panel bg
  problemCard3PanelBg: "hsl(220 75% 96%)", // card 3 (sky) viz panel bg

  /* Loop product-card macOS traffic-light dots (design L272-274). These are
     desaturated chrome dots, NOT accent colors. */
  trafficLightRed: "hsl(0 60% 70%)", // L272
  trafficLightAmber: "hsl(42 80% 65%)", // L273
  trafficLightGreen: "hsl(150 45% 60%)", // L274

  /* Loop product-card + dark-section surfaces. */
  loopCardSurface: "hsl(40 30% 98%)", // light card surface inside dark loop (L270)
  darkBg: "hsl(225 50% 3.5%)", // loop + payer-wall section background (L197, L344)
  darkBg2: "hsl(225 50% 5.5%)", // payer pill background (L1028)
} as const;
